import { css } from 'astroturf';
import React from 'react';
import { Checkmark } from 'react-bytesize-icons';
import CatalogCard from './_catalogCardLayout';
import SnippetInfoDialog from './_snippetInfoDialog';
import { TypeBadge } from './_typeBadge';

const styles = css`
  .categories {
    position: relative;
    display: flex;
    gap: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    margin: 0.25rem 0 0.25rem -0.5rem;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 2rem;
      height: 100%;
      background: linear-gradient(to left, #fff, #fff0);
      pointer-events: none;
    }
  }

  .category {
    font-size: 10px;
    border-radius: 100px;
    padding: 0.1rem 0.55rem;
    background: #00aeff21;
    color: #333;
  }

  .cardHeader {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    h4 {
      margin: 0;
      line-height: 1.5;
      color: #0a1530;
      min-width: 0;
      display: block;

      small {
        font-size: 0.75em;
        color: #39728d;
        font-weight: 400;
      }
    }

    p {
      margin: 0;
      line-height: 1.25;
      font-size: 0.875em;
      text-wrap: pretty;
      line-clamp: 2;
      text-overflow: ellipsis;
      overflow: hidden;

      &.author {
        margin-top: 0.5rem;
        opacity: 0.925;
        font-size: 0.75rem;

        b {
          font-weight: 500;
          color: #39728d;
        }
      }
    }
  }

  .nameButton {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
    display: inline;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
    }
  }

  .add {
    margin-left: auto;
    flex: 0 0 2.3rem;
    min-width: 2.3rem;
    background: #0a153011;
    color: #39728d;
    font-size: 2rem;
    line-height: 1;
    padding: 0;
    border: 0;
    border-radius: 4px;
    transition:
      background 0.15s ease-in-out,
      color 0.15s ease-in-out;

    &:hover,
    &:focus-visible {
      background: #0a1530;
      color: #fff;
    }
  }

  .active {
    background: #008300;
    color: #fff;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const SnippetCard = React.memo(
  ({
    snippet,
    isInCollection,
    onAdd,
    onDownload,
    normalizeType,
    hideActions = false,
    collectionName = null,
    activeSnippet = null,
    activeCollection = null,
  }) => (
    <CatalogCard
      onDownload={() => onDownload(snippet)}
      type="snippet"
      jsonData={snippet}
      hideActions={hideActions}
      extraActions={
        <button
          className={[styles.add, isInCollection ? styles.active : ''].join(' ')}
          type="button"
          aria-label={isInCollection ? 'Remove from collection' : 'Add to collection'}
          data-tip={isInCollection ? 'Remove from collection' : 'Add to collection'}
          onClick={() => onAdd(snippet)}
        >
          {isInCollection ? <Checkmark /> : '+'}
        </button>
      }
    >
      <TypeBadge language={snippet.language} normalizeType={normalizeType} position="absolute" />
      <div className={styles.cardHeader}>
        <SnippetInfoDialog
          snippet={snippet}
          normalizeType={normalizeType}
          onDownload={onDownload}
          collectionName={collectionName}
          activeSnippet={activeSnippet}
          activeCollection={activeCollection}
          renderTrigger={(show) => (
            <h4>
              <button type="button" className={styles.nameButton} onClick={show} data-tip="View snippet details">
                {snippet.name}
              </button>
            </h4>
          )}
        />
        {!!(snippet.categories || []).length && (
          <div className={styles.categories}>
            {(snippet.categories || []).map((category) => (
              <span key={category} className={styles.category}>
                {category}
              </span>
            ))}
          </div>
        )}
        {snippet.description && <p>{snippet.description}</p>}
        {snippet.author !== 'Unknown' ? (
          <p className={styles.author}>
            By{' '}
            {snippet.authorUrl ? (
              <a href={snippet.authorUrl} target="_blank" rel="noopener noreferrer">
                <b>{snippet.author}</b>
              </a>
            ) : (
              <b>{snippet.author}</b>
            )}
          </p>
        ) : null}
      </div>
    </CatalogCard>
  )
);

export default SnippetCard;
